import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

function PasswordRecovery() {
  const navigate = useNavigate();
  const [step, setStep] = useState('choose'); // choose, forgot-password, recover-email, reset-password
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [foundAccounts, setFoundAccounts] = useState([]);

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    try {
      const response = await axios.post(`${API_URL}/auth/forgot-password`, { email });
      setMessage(`✅ ${response.data.message}`);
      if (response.data.verification_code) {
        setMessage(prev => prev + `\n\n🔐 Code de vérification: ${response.data.verification_code}\n(En production, ce code serait envoyé par email)`);
      }
      setStep('reset-password');
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la demande');
    }
  };

  const handleRecoverEmail = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    try {
      const response = await axios.get(
        `${API_URL}/auth/recover-email?first_name=${encodeURIComponent(firstName)}&last_name=${encodeURIComponent(lastName)}`
      );
      setFoundAccounts(response.data.accounts);
      setMessage(`✅ ${response.data.message}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Aucun compte trouvé');
      setFoundAccounts([]);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (newPassword !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    if (newPassword.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    try {
      await axios.post(`${API_URL}/auth/reset-password`, {
        email,
        verification_code: verificationCode,
        new_password: newPassword
      });
      setMessage('✅ Mot de passe réinitialisé avec succès !');
      setTimeout(() => navigate('/auth'), 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la réinitialisation');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <img 
            src="/logo-bkb.jpg" 
            alt="Bank Kreiz Breizh" 
            className="w-20 h-20 rounded-xl mx-auto mb-4 object-cover shadow-2xl border-2 border-yellow-400"
          />
          <h1 className="text-3xl font-bold mb-2">Récupération de compte</h1>
          <p className="text-gray-300">Bank Kreiz Breizh</p>
        </div>

        {/* Choice Step */}
        {step === 'choose' && (
          <div className="bg-white rounded-2xl p-8 text-gray-900">
            <h2 className="text-2xl font-bold mb-6 text-center">Que souhaitez-vous faire ?</h2>
            
            <div className="space-y-4">
              <button
                onClick={() => setStep('forgot-password')}
                className="w-full p-6 bg-blue-50 hover:bg-blue-100 border-2 border-blue-200 rounded-xl transition-colors text-left"
              >
                <div className="flex items-start gap-4">
                  <span className="text-3xl">🔑</span>
                  <div>
                    <h3 className="text-lg font-bold mb-1">J'ai oublié mon mot de passe</h3>
                    <p className="text-sm text-gray-600">Je connais mon email mais j'ai oublié mon mot de passe</p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => setStep('recover-email')}
                className="w-full p-6 bg-green-50 hover:bg-green-100 border-2 border-green-200 rounded-xl transition-colors text-left"
              >
                <div className="flex items-start gap-4">
                  <span className="text-3xl">📧</span>
                  <div>
                    <h3 className="text-lg font-bold mb-1">J'ai oublié mon email</h3>
                    <p className="text-sm text-gray-600">Je ne me souviens plus de mon email de connexion</p>
                  </div>
                </div>
              </button>
            </div>

            <button
              onClick={() => navigate('/auth')}
              className="w-full mt-6 px-6 py-3 bg-gray-200 hover:bg-gray-300 rounded-lg font-medium transition-colors"
            >
              ← Retour à la connexion
            </button>
          </div>
        )}

        {/* Forgot Password Step */}
        {step === 'forgot-password' && (
          <div className="bg-white rounded-2xl p-8 text-gray-900">
            <h2 className="text-2xl font-bold mb-6">Réinitialiser le mot de passe</h2>
            
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="votre.email@exemple.com"
                  required
                />
              </div>

              {message && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-sm whitespace-pre-line">
                  {message}
                </div>
              )}

              {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  {error}
                </div>
              )}

              <button
                type="submit"
                className="w-full px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors"
              >
                Envoyer le code de vérification
              </button>

              <button
                type="button"
                onClick={() => { setStep('choose'); setMessage(''); setError(''); }}
                className="w-full px-6 py-3 bg-gray-200 hover:bg-gray-300 rounded-lg font-medium transition-colors"
              >
                ← Retour
              </button>
            </form>
          </div>
        )}

        {/* Recover Email Step */}
        {step === 'recover-email' && (
          <div className="bg-white rounded-2xl p-8 text-gray-900">
            <h2 className="text-2xl font-bold mb-6">Retrouver mon email</h2>
            
            <form onSubmit={handleRecoverEmail} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Prénom</label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Jean"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Nom de famille</label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Dupont"
                  required
                />
              </div>

              {message && !error && foundAccounts.length > 0 && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="font-semibold mb-3">{message}</p>
                  <div className="space-y-2">
                    {foundAccounts.map((account, index) => (
                      <div key={index} className="p-3 bg-white rounded border border-gray-200">
                        <p className="font-medium">📧 {account.email}</p>
                        <p className="text-sm text-gray-600">Rôle: {account.role}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  {error}
                </div>
              )}

              <button
                type="submit"
                className="w-full px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold transition-colors"
              >
                Rechercher mon compte
              </button>

              <button
                type="button"
                onClick={() => { setStep('choose'); setMessage(''); setError(''); setFoundAccounts([]); }}
                className="w-full px-6 py-3 bg-gray-200 hover:bg-gray-300 rounded-lg font-medium transition-colors"
              >
                ← Retour
              </button>
            </form>
          </div>
        )}

        {/* Reset Password Step */}
        {step === 'reset-password' && (
          <div className="bg-white rounded-2xl p-8 text-gray-900">
            <h2 className="text-2xl font-bold mb-6">Nouveau mot de passe</h2>
            
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Code de vérification</label>
                <input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-center text-2xl tracking-widest font-mono"
                  placeholder="000000"
                  maxLength="6"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Entrez le code à 6 chiffres reçu</p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Nouveau mot de passe</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Minimum 6 caractères"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Confirmer le mot de passe</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Retapez le mot de passe"
                  required
                />
              </div>

              {message && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-sm">
                  {message}
                </div>
              )}

              {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  {error}
                </div>
              )}

              <button
                type="submit"
                className="w-full px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors"
              >
                Réinitialiser le mot de passe
              </button>

              <button
                type="button"
                onClick={() => { setStep('forgot-password'); setMessage(''); setError(''); }}
                className="w-full px-6 py-3 bg-gray-200 hover:bg-gray-300 rounded-lg font-medium transition-colors"
              >
                ← Retour
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}

export default PasswordRecovery;
